// "Did you know" hints (Features 4, onboarding part 4): after a person does something the slow way, one quiet line on
// the faster way, in the corner where the board's notes show (a toast, never over what they are doing), dismissible.
// Each shows once ever for the account (shared/onboarding.ts hints, on every device), at most one a day, never during
// a tour of tips, and never once Settings' Show tips is off. What a person did is counted here in this tab, and the
// only thing ever written is that a hint was shown.
//
// The triggers, each its own id:
// - arrange-board: on a canvas with 6 or more widgets, 8 widgets dragged one at a time within 3 minutes.
// - deals-chat: on a client pipeline, 4 deals added by hand, one at a time, within 15 minutes.
// - slides-chat: on a slides board, 4 slides added by hand, one at a time, within 15 minutes.
// - model-menu: a default model changed in Settings on 2 visits to Settings within 14 days (kept in this browser).

import { kindOf } from "../../shared/boardKinds";
import type { Entry, ID, Op } from "../../shared/types";
import { openChat } from "../agent/store";
import { committed } from "../state/actions";
import { isOn, onboarding, sawHint } from "../state/onboarding";
import { get, toast, useStore } from "../state/store";
import { quietForChecks, useTour } from "./tips";
import { readStoredJson, writeStoredJson } from "../lib/stored";
import { DAY } from "../../shared/period";

export type HintId = "arrange-board" | "deals-chat" | "slides-chat" | "model-menu";

interface HintSpec {
  title: string;
  body: string;
  action: { label: string; run: () => void };
}

export const HINTS: Record<HintId, HintSpec> = {
  "arrange-board": {
    title: "Ask the chat to arrange this board",
    body: "It sets every widget under its heading in one step. “Put it back” undoes it.",
    action: { label: "Ask the chat", run: () => openChat("Arrange this board into sections", { send: false }) },
  },
  "deals-chat": {
    title: "Add a list of clients in one go",
    body: "Type or paste them in the chat, with what you know about each, and it adds every deal.",
    action: { label: "Ask the chat", run: () => openChat("Add these clients: ", { send: false }) },
  },
  "slides-chat": {
    title: "Let your agent draft the slides",
    body: "Say what the deck is for and it writes every slide. You change any of them after.",
    action: { label: "Ask the chat", run: () => openChat("Draft five slides about ", { send: false }) },
  },
  "model-menu": {
    title: "Change the model from the chat",
    body: "The chat's model menu picks one for that chat, and Set as default keeps it for new chats.",
    action: { label: "Open the chat", run: () => openChat() },
  },
};

/** Whether a hint may show now: tips on, this one never shown, none in the last day, no tour on screen. */
export function hintAllowed(id: HintId, now = Date.now()): boolean {
  const s = get();
  if (s.guest || !s.workspace || s.presenting || quietForChecks()) return false;
  const o = onboarding();
  if (isOn(o.tipsOff)) return false;
  if (o.hints?.[id]) return false;
  if (Object.values(o.hints ?? {}).some((t) => now - t < DAY)) return false;
  return !useTour.getState().kind;
}

/** Shows a hint, once, when it may. */
export function showHint(id: HintId): boolean {
  if (!hintAllowed(id)) return false;
  const h = HINTS[id];
  sawHint(id);
  toast({ level: "info", title: h.title, body: h.body, action: h.action, key: "hint", ms: 14_000 });
  return true;
}

/** Times something happened, the newest kept, for a count within a window. */
class Recent {
  private at: number[] = [];
  constructor(private readonly n: number, private readonly ms: number) {}
  add(now = Date.now()): boolean {
    this.at = [...this.at.filter((t) => now - t < this.ms), now];
    return this.at.length >= this.n;
  }
}

const drags = new Recent(8, 3 * 60_000);
const deals = new Recent(4, 15 * 60_000);
const slides = new Recent(4, 15 * 60_000);

/** One of the user's own changes, as it is made: is it the slow way? */
function watch(ops: Op[], label: string, before: Record<ID, Entry>) {
  const s = get();
  const board = s.boards.find((b) => b.id === s.boardId);
  const kind = kindOf(board);
  if (kind === "canvas" && label === "Move" && ops.length === 1 && ops[0].op === "item.put") {
    const widgets = Object.values(s.items).filter((i) => i.boardId === s.boardId && i.kind === "widget").length;
    if (widgets >= 6 && drags.add()) showHint("arrange-board");
    return;
  }
  const made = ops.filter((o): o is Extract<Op, { op: "entry.put" }> => o.op === "entry.put" && !before[o.entry.id]);
  if (made.length !== 1) return;
  if (kind === "pipeline" && made[0].entry.t === "deal" && deals.add()) showHint("deals-chat");
  if (kind === "slides" && made[0].entry.t === "slide" && slides.add()) showHint("slides-chat");
}

const MODEL_KEY = "cb:hint:model-visits";

/** A default model changed while Settings is open: one visit to Settings counts once. */
function watchModels() {
  let visit = 0;
  let counted = -1;
  let was = JSON.stringify(get().runner?.agentModels ?? null);
  useStore.subscribe((s, prev) => {
    if (s.panel === "settings" && prev.panel !== "settings") visit++;
    const now = JSON.stringify(s.runner?.agentModels ?? null);
    if (now === was) return;
    const changed = was !== "null";
    was = now;
    if (!changed || s.panel !== "settings" || counted === visit) return;
    counted = visit;
    const kept = readStoredJson<unknown>(MODEL_KEY, []);
    const times = (Array.isArray(kept) ? kept : []).filter((t): t is number => typeof t === "number" && Date.now() - t < 14 * DAY);
    times.push(Date.now());
    writeStoredJson(MODEL_KEY, times.slice(-5)); // private mode: this visit alone counts
    if (times.length >= 2) showHint("model-menu");
  });
}

let started = false;
/** Starts watching, once, when the board has loaded. */
export function startHints() {
  if (started) return;
  started = true;
  committed.add(watch);
  watchModels();
}
